import Box from '@mui/material/Box';
import { colors } from '@sweep-io/sweep-design';
import { useResizeDrag } from '../../../../hooks/useResizeDrag';
import { useCustomColumnsTableContext } from './CustomColumnsTableContext';
import { DataTableBaseColumn } from '../TableTypes';

export const CELL_MIN_WIDTH = 80;

interface ResizeHandleProps {
  column: DataTableBaseColumn;
  cellRef: React.RefObject<HTMLTableCellElement>;
}

export const ResizeHandle = ({ column, cellRef }: ResizeHandleProps) => {
  const {
    columnWidths,
    setOverResizeHandleColumnId,
    overResizeHandleColumnId,
    setResizingColumnId,
    onColumnResize,
    onColumnResizeEndCallback,
  } = useCustomColumnsTableContext();

  // Use the reusable resize drag hook
  const { isResizing, handleResizeStart, handleMouseEnter, handleMouseLeave } = useResizeDrag({
    onResize: (newWidth) => onColumnResize?.({ columnId: column.field, width: newWidth }),
    onResizeEnd: () => onColumnResizeEndCallback?.(),
    minValue: CELL_MIN_WIDTH,
  });

  const handleMouseEnterWithContext = () => {
    handleMouseEnter();
    setOverResizeHandleColumnId?.(column.field);
  };

  const handleMouseLeaveWithContext = () => {
    handleMouseLeave();
    if (!isResizing) {
      setOverResizeHandleColumnId?.(null);
    }
  };

  const handleResizeStartWithContext = (e: React.MouseEvent) => {
    if (cellRef.current) {
      const initialWidth = columnWidths?.[column.field] ?? cellRef.current.clientWidth;
      setResizingColumnId?.(column.field);
      handleResizeStart(e, initialWidth);
    }
  };

  return (
    <Box
      className="resize-handle"
      sx={{
        position: 'absolute',
        right: '0px',
        top: 0,
        bottom: 0,
        width: '2px',
        cursor: 'col-resize',
        zIndex: 400,
        transition: 'opacity 0.2s ease-in-out, background-color 0.2s ease-in-out',
        backgroundColor:
          overResizeHandleColumnId === column.field || isResizing
            ? colors.blue[500]
            : 'transparent',
        opacity: overResizeHandleColumnId === column.field || isResizing ? 1 : 0,
      }}
      onMouseDown={handleResizeStartWithContext}
      onMouseEnter={(e) => {
        e.stopPropagation();
        handleMouseEnterWithContext();
      }}
      onMouseLeave={(e) => {
        e.stopPropagation();
        handleMouseLeaveWithContext();
      }}
      onClick={(e) => {
        //to prevent any row click event from being triggered
        e.stopPropagation();
      }}
    />
  );
};
